import { useState, useRef, useEffect } from "react";
import { Button } from "src/components/ui/button";
import { Steps } from "antd";
import { ArrowLeft } from "lucide-react";
import dayjs from "dayjs";
import TeacherStep from "./Steps/TeacherStep/TeacherStep";
import TimeStep from "./Steps/TimeStep/TimeStep";
import PriceStep from "./Steps/PriceStep/PriceStep";
import BookingStep from "./Steps/BookingStep/BookingStep";
import LevelStep from "./Steps/LevelStep/LevelStep";

export default function EnrollmentProcess({
  course,
  onBackToList,
  setShowEnrollment,
}) {
  // --------------------------
  // State Declarations
  // --------------------------
  const [selectedTeacher, setSelectedTeacher] = useState(
    JSON.parse(localStorage.getItem("SelectedTeacher")) || null
  );

  const [selectedDate, setSelectedDate] = useState(
    localStorage.getItem("SelectedDate") || ""
  );
  const [selectedSlots, setSelectedSlots] = useState(
    JSON.parse(localStorage.getItem("SelectedSlots")) || []
  );

  const [selectedLevel, setSelectedLevel] = useState(
    JSON.parse(localStorage.getItem("SelectedLevel")) || null
  );

  const [selectedPrice, setSelectedPrice] = useState(
    JSON.parse(localStorage.getItem("SelectedPrice")) || null
  );

  const [currentStep, setCurrentStep] = useState(
    +localStorage.getItem("CurrentStep") || 0
  );

  // --------------------------
  // Helper Functions
  // --------------------------
  useEffect(() => {
    currentStep && localStorage.setItem("CurrentStep", currentStep);
    selectedLevel &&
      localStorage.setItem("SelectedLevel", JSON.stringify(selectedLevel));
    selectedPrice &&
      localStorage.setItem("SelectedPrice", JSON.stringify(selectedPrice));
    selectedTeacher &&
      localStorage.setItem("SelectedTeacher", JSON.stringify(selectedTeacher));
    selectedDate && localStorage.setItem("SelectedDate", selectedDate);
    selectedSlots &&
      localStorage.setItem("SelectedSlots", JSON.stringify(selectedSlots));
  }, [
    currentStep,
    selectedLevel,
    selectedPrice,
    selectedTeacher,
    selectedDate,
    selectedSlots,
  ]);

  const handleLevelSelect = (level) => {
    setSelectedLevel(level);
    setSelectedTeacher(null);
    setSelectedSlots(null);
    setSelectedPrice(null);
    setCurrentStep(1);
  };

  const handleTeacherSelect = (teacher) => {
    setSelectedTeacher(teacher);
    setSelectedSlots(null);
    setSelectedPrice(null);
    setCurrentStep(2);
  };

  // --------------------------
  // Booking Handler
  // --------------------------
  const resetBooking = () => {
    onBackToList();
  };

  // --------------------------
  // Render Component
  // --------------------------
  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="flex items-center mb-8">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            onBackToList();
            setShowEnrollment(false);
          }}
          className="mr-4"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-3xl font-bold">
          Enroll in:{" "}
          <span className="text-blue-600">
            {course?.title?.en || course?.title?.ar || "Selected Course"}
          </span>
        </h1>
      </div>

      {/* Process Steps */}
      <div className="mb-8">
        <Steps
          current={currentStep}
          items={[
            { title: "Select Level" },
            { title: "Select Teacher" },
            { title: "Select Date & Time" },
            { title: "Select Price" },
            { title: "Confirm" },
          ].map((step, index) => ({
            title: step.title,
            status:
              index === currentStep
                ? "process"
                : index < currentStep
                ? "finish"
                : "wait",
          }))}
        />
      </div>

      {/* Step 1: Level Selection */}
      {currentStep === 0 && (
        <LevelStep
          course={course}
          selectedLevel={selectedLevel}
          handleLevelSelect={handleLevelSelect}
        />
      )}

      {/* Step 2: Teacher Selection */}
      {currentStep === 1 && (
        <TeacherStep
          course={course}
          selectedTeacher={selectedTeacher}
          handleTeacherSelect={handleTeacherSelect}
          setCurrentStep={setCurrentStep}
        />
      )}

      {/* Step 3: Pricing Selection */}
      {currentStep === 2 && (
        <PriceStep
          setCurrentStep={setCurrentStep}
          selectedTeacher={selectedTeacher}
          selectedPrice={selectedPrice}
          setSelectedPrice={setSelectedPrice}
        />
      )}

      {/* Step 4: Date & Time Selection */}
      {currentStep === 3 && (
        <TimeStep
          selectedTeacher={selectedTeacher}
          setCurrentStep={setCurrentStep}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          selectedSlots={selectedSlots || []}
          setSelectedSlots={setSelectedSlots}
          selectedPrice={selectedPrice}
        />
      )}

      {/* Step 5: Booking Confirmation */}
      {currentStep === 4 && (
        <BookingStep
          course={course}
          selectedTeacher={selectedTeacher}
          selectedDate={selectedDate}
          selectedSlots={selectedSlots}
          selectedPrice={selectedPrice}
          selectedLevel={selectedLevel}
          resetBooking={resetBooking}
          setCurrentStep={setCurrentStep}
          setShowEnrollment={setShowEnrollment}
        />
      )}
    </div>
  );
}
